import clsx from 'clsx';
import { createPortal } from 'react-dom';

import { LanguageSwitcherDropdown } from '@features/languageSwitcher';
import { LogOutButton } from '@features/logOut';

import { ArrowLeftIcon } from '@shared/assets/icons';
import { Badge, BADGE_SHAPE, BADGE_VARIANT } from '@shared/ui/badge';
import { TabButton } from '@shared/ui/tabButton';

import s from './HeaderNavigation.module.css';

const getMenuLabelClassName = ({ isActive }) =>
	clsx(isActive ? 'body-semibold-base' : 'body-medium-base');

export const HeaderNavigationDropdown = ({
	activeItem,
	switchItems,
	showAllCompaniesButton,
	primaryCompanyItem,
	companyCount,
	flyoutTriggerRef,
	onFlyoutToggle,

	switchToLabel,
	allCompaniesLabel,
	floatingRef,
	floatingStyles,
}) => {
	const ActiveIcon = activeItem.icon;
	const PrimaryCompanyIcon = primaryCompanyItem?.icon ?? null;

	return createPortal(
		<div
			ref={floatingRef}
			style={{ ...floatingStyles, zIndex: 1200 }}
			className={clsx('nav-dropdown', s.dropdownPanel)}
			role='menu'
		>
			<div className='nav-dropdown__section'>
				<button
					type='button'
					className={clsx('nav-item', 'nav-item--dropdown', 'nav-item--active')}
					onClick={activeItem.onClick}
					role='menuitem'
				>
					<div className='nav-item__wrap'>
						<ActiveIcon className='nav-item__icon' aria-hidden='true' focusable='false' />
						<span className={clsx(getMenuLabelClassName({ isActive: true }), 'nav-item__title')}>
							{activeItem.label}
						</span>
					</div>

					<span className='dot dot--blue' aria-hidden='true'>
						<span className='dot__circle' />
					</span>
				</button>
			</div>

			{switchItems.length > 0 && (
				<>
					<div className='nav-dropdown__separator' />
					<div className='nav-dropdown__section'>
						<p className='body-medium-sm nav-dropdown__switch-header'>{switchToLabel}</p>

						{showAllCompaniesButton ? (
							<>
								{switchItems
									.filter((item) => item.id === 'private')
									.map(({ id, label, isActive, icon: Icon, onClick }) => (
										<TabButton
											key={id}
											leftIcon={Icon}
											label={label}
											className={clsx(
												'nav-item',
												'nav-item--dropdown',
												isActive && 'nav-item--active',
											)}
											labelClassName={clsx(getMenuLabelClassName({ isActive }), 'nav-item__title')}
											iconClassName='nav-item__icon'
											onClick={onClick}
											role='menuitem'
										/>
									))}

								{primaryCompanyItem && (
									<button
										type='button'
										className='nav-item nav-item--dropdown'
										onClick={primaryCompanyItem.onClick}
										role='menuitem'
									>
										<div className='nav-item__wrap'>
											<PrimaryCompanyIcon
												className='nav-item__icon'
												aria-hidden='true'
												focusable='false'
											/>
											<span
												className={clsx(
													getMenuLabelClassName({ isActive: false }),
													'nav-item__title',
												)}
											>
												{primaryCompanyItem.label}
											</span>
										</div>
										<span className='dot dot--green' aria-hidden='true'>
											<span className='dot__circle' />
										</span>
									</button>
								)}

								<button
									ref={flyoutTriggerRef}
									type='button'
									className='nav-item nav-item--dropdown'
									onClick={onFlyoutToggle}
									role='menuitem'
								>
									<div className='nav-item__wrap'>
										<span
											className={clsx(
												getMenuLabelClassName({ isActive: false }),
												'nav-item__title',
											)}
										>
											{allCompaniesLabel}
										</span>
									</div>

									<Badge
										label={String(companyCount)}
										shape={BADGE_SHAPE.pill}
										variant={BADGE_VARIANT.infoGray}
									/>
									<ArrowLeftIcon className='nav-item__icon' aria-hidden='true' focusable='false' />
								</button>
							</>
						) : (
							switchItems.map(({ id, label, isActive, icon: Icon, onClick }) =>
								id === primaryCompanyItem?.id ? (
									<button
										key={id}
										type='button'
										className='nav-item nav-item--dropdown'
										onClick={onClick}
										role='menuitem'
									>
										<div className='nav-item__wrap'>
											<Icon className='nav-item__icon' aria-hidden='true' focusable='false' />
											<span
												className={clsx(
													getMenuLabelClassName({ isActive: false }),
													'nav-item__title',
												)}
											>
												{label}
											</span>
										</div>
										<span className='dot dot--green' aria-hidden='true'>
											<span className='dot__circle' />
										</span>
									</button>
								) : (
									<TabButton
										key={id}
										leftIcon={Icon}
										label={label}
										className={clsx(
											'nav-item',
											'nav-item--dropdown',
											isActive && 'nav-item--active',
										)}
										labelClassName={clsx(getMenuLabelClassName({ isActive }), 'nav-item__title')}
										iconClassName='nav-item__icon'
										onClick={onClick}
										role='menuitem'
									/>
								),
							)
						)}
					</div>
				</>
			)}

			<div className='nav-dropdown__separator' />

			<LogOutButton />

			<div className='nav-dropdown__separator' />

			<LanguageSwitcherDropdown />
		</div>,
		document.body,
	);
};
